import chroma from "chroma-js";
import { scaleThreshold } from "d3-scale";
import L from "leaflet";
import * as ReactDOMServer from "react-dom/server";
import ss from "simple-statistics";
import _ from "underscore";

import { isPK } from "metabase-lib/v1/types/utils/isa";

import DotIcon from "./DotIcon";
import { LeafletMap } from "./LeafletMap";

const createIcon = color => {
  return L.divIcon({
    className: `${color} marker-icon`,
    html: ReactDOMServer.renderToString(<DotIcon color={color} />),
    iconSize: [24, 24],
  });
};

const getMarkerClass = marker => {
  try {
    return marker.options.icon.options.className;
  } catch {
    return "";
  }
};

const SECTIONS = 10;

export default class LeafletDotMap extends LeafletMap {
  componentDidMount() {
    super.componentDidMount();

    this.pinMarkerLayer = L.layerGroup([]).addTo(this.map);

    this.componentDidUpdate({}, {});
  }

  componentDidUpdate(prevProps, prevState) {
    super.componentDidUpdate(prevProps, prevState);

    try {
      const { pinMarkerLayer } = this;
      const { settings, points } = this.props;

      const domain = points.map(point => point[2]); // point[2] contains metric
      let groupBoundaries = domain;

      const _heatMapColors = settings["map.dot.colors"];
      if (!_heatMapColors) {
        return;
      }
      const heatMapColors = _heatMapColors.slice(-domain.length);
      const colorValues = chroma.scale(heatMapColors).colors(SECTIONS);
      // if less than 10 datapoints do not use ck means
      if (domain.length > 10) {
        // ckmeans used to break data up into 10 clusters
        const groups = ss.ckmeans(domain, colorValues.length);
        groupBoundaries = groups.slice(1).map(cluster => cluster[0]);
      }
      const colorScale = scaleThreshold() // swap this function to change how data is divided
        .domain(groupBoundaries)
        .range(colorValues);

      const markers = pinMarkerLayer.getLayers();
      const max = Math.max(points.length, markers.length);

      if (points.length < 1) {
        throw "No data available to map";
      }
      for (let i = 0; i < max; i++) {
        // remove extra markers
        if (i >= points.length) {
          pinMarkerLayer.removeLayer(markers[i]);
        }
        // catch undefined destructuring error
        if (points[i] === undefined) {
          continue;
        }

        const [lng, lat, metric] = points[i];
        const color = colorScale(metric);

        if (i < markers.length && !getMarkerClass(markers[i]).includes(color)) {
          // replace icon with icon of new color
          pinMarkerLayer.removeLayer(markers[i]);
          const icon = createIcon(colorScale(metric));
          const marker = this._createMarker(i, icon);
          pinMarkerLayer.addLayer(marker);
          markers[i] = marker;
        } else if (i >= markers.length) {
          // make new icon
          const icon = createIcon(colorScale(metric));
          const marker = this._createMarker(i, icon);
          pinMarkerLayer.addLayer(marker);
          markers.push(marker);
        }

        if (i < points.length) {
          // update position of marker as required
          const { curLat, curLng } = markers[i].getLatLng();
          if (curLng !== lng || curLat !== lat) {
            markers[i].setLatLng([lng, lat]);
          }
        }
      }
    } catch (err) {
      console.error(err);
      this.props.onRenderError(err.message || err);
    }
  }

  _createMarker = (rowIndex, icon) => {
    const marker = L.marker([0, 0], { icon });
    const { onHoverChange, onVisualizationClick, settings } = this.props;
    if (onHoverChange) {
      marker.on("mousemove", e => {
        const {
          series: [
            {
              data: { cols, rows },
            },
          ],
        } = this.props;
        const hover = {
          dimensions: cols.map((col, colIndex) => ({
            value: rows[rowIndex][colIndex],
            column: col,
          })),
          element: marker._icon,
        };
        onHoverChange(hover);
      });
      marker.on("mouseout", () => {
        onHoverChange(null);
      });
    }
    if (onVisualizationClick) {
      marker.on("click", () => {
        const {
          series: [
            {
              data: { cols, rows },
            },
          ],
        } = this.props;
        // if there is a primary key then associate a pin with it
        const pkIndex = _.findIndex(cols, isPK);
        const hasPk = pkIndex >= 0;

        const data = cols.map((col, index) => ({
          col,
          value: rows[rowIndex][index],
        }));

        onVisualizationClick({
          value: hasPk ? rows[rowIndex][pkIndex] : null,
          column: hasPk ? cols[pkIndex] : null,
          element: marker._icon,
          origin: { row: rows[rowIndex], cols },
          settings,
          data,
        });
      });
    }
    return marker;
  };
}
